import React, { useState, useEffect, ReactElement, useContext } from "react";
import { Box, FormControlLabel, Stack, Switch } from "@mui/material";
import Grid from "@mui/material/Grid2";
import OmissionComponent from "../components/OmissionComponent";
import LeaveComponent from "../components/LeaveComponent";
import BirthdayJubileeComponent from "../components/BirthdayJubileeComponent";
import RecruitmentComponent from "../components/RecruitmentComponent";
import AnnualInterviewsComponent from "../components/AnnualInterviewsComponent";
import Bezetting from "./Bezetting";
import { LanguageContext } from "../context/LanguageContext";
import { RoleContext } from "../context/RoleContext";

// Define interfaces for our types
interface GridItem {
  id: number;
  size: {
    xs?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  component: ReactElement;
}

function Index(): ReactElement {
  const { strings } = useContext(LanguageContext);
  const role = useContext(RoleContext);
  // Define the default grid components with their sizes
  const defaultGridItems: GridItem[] = [
    { id: 1, size: { xs: 12, md: 6, lg: 3 }, component: <OmissionComponent /> },
    { id: 2, size: { xs: 12, md: 6, lg: 5 }, component: <LeaveComponent /> },
    { id: 3, size: { xs: 12, md: 6, lg: 4 }, component: <BirthdayJubileeComponent /> },
    {
      id: 4,
      size: { xs: 12, md: role === "secretary" ? 12 : 6, lg: role === "secretary" ? 12 : 6 },
      component: <RecruitmentComponent />,
    },
    ...(role !== "secretary"
      ? [
          {
            id: 5,
            size: { xs: 12, md: 6, lg: 6 },
            component: <AnnualInterviewsComponent />,
          },
        ]
      : []),
    {
      id: 6,
      size: { xs: 12, md: 12, lg: 12 },
      component: (
        <Box mt={2}>
          <Bezetting />
        </Box>
      ),
    },
  ];

  // State to store the current order of items
  const [gridItems, setGridItems] = useState<GridItem[]>([]);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  // Load the saved order on component mount
  useEffect(() => {
    console.log("Loading saved grid order...");
    const savedOrder = getCookie("gridOrder");

    if (savedOrder) {
      try {
        console.log("Found saved order:", savedOrder);
        const parsedOrder: number[] = JSON.parse(savedOrder);

        // Check if the saved order contains valid ids
        const isValidOrder = parsedOrder.every((id) => defaultGridItems.some((item) => item.id === id));

        if (isValidOrder && parsedOrder.length === defaultGridItems.length) {
          console.log("Saved order is valid, applying it");
          // Reconstruct the items array based on the saved order
          const orderedItems = parsedOrder.map(
            (id) => defaultGridItems.find((item) => item.id === id) as GridItem
          );
          setGridItems(orderedItems);
          return;
        } else {
          console.warn("Saved order is invalid or incomplete");
        }
      } catch (e) {
        console.error("Error parsing saved grid order", e);
      }
    } else {
      console.log("No saved order found");
    }

    // If no valid saved order, use the default order
    console.log("Using default grid order");
    setGridItems(defaultGridItems);
  }, []);

  // Save the current order to localStorage
  const saveOrderToCookie = (newOrder: GridItem[]): void => {
    const orderIds = newOrder.map((item) => item.id);
    setCookie("gridOrder", JSON.stringify(orderIds));
  };

  // Simple storage for the grid order using localStorage
  // This works reliably within iframes regardless of cross-origin restrictions

  // Save the grid order
  const saveGridOrder = (name: string, value: string): void => {
    try {
      localStorage.setItem(`grid_${name}`, value);
      console.log("Grid order saved to localStorage");
    } catch (e) {
      console.log("localStorage not available:", e);
    }
  };

  // Retrieve the grid order
  const retrieveGridOrder = (name: string): string | null => {
    try {
      const localValue = localStorage.getItem(`grid_${name}`);
      if (localValue) {
        console.log("Grid order retrieved from localStorage");
        return localValue;
      }
    } catch (e) {
      console.log("localStorage not available:", e);
    }
    return null;
  };

  // Use these functions with the existing code
  const setCookie = saveGridOrder;
  const getCookie = retrieveGridOrder;

  // Handle the start of a drag operation
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number): void => {
    setDraggedItem(index);
    // Store the dragged index for Firefox compatibility
    e.dataTransfer.setData("text/plain", index.toString());
    e.currentTarget.classList.add("dragging");
  };

  // Handle the end of a drag operation
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
    e.currentTarget.classList.remove("dragging");
    setDraggedItem(null);
  };

  // Handle drag enter to give visual feedback
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-over");
  };

  // Handle drag leave to remove visual feedback
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");
  };

  // Handle an item being dropped onto another item
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number): void => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");

    const dragIndex = draggedItem;
    if (dragIndex === null || dragIndex === dropIndex) return;

    // Reorder the items
    const newItems = [...gridItems];
    const draggedItemContent = newItems[dragIndex];
    newItems.splice(dragIndex, 1);
    newItems.splice(dropIndex, 0, draggedItemContent);

    setGridItems(newItems);
    saveOrderToCookie(newItems);
  };

  // Handle the dragover event (needed to allow dropping)
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  // Add some styles for drag and drop visual feedback
  const dragStyles = `
    .grid-item {
      transition: transform 0.2s, box-shadow 0.2s;
      position: relative;
    }
    .grid-item.dragging {
      opacity: 0.5;
      transform: scale(0.98);
    }
    .grid-item.drag-over {
      transform: scale(1.02);
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    }
    .grid-item .drag-handle {
      position: absolute;
      top: 8px;
      right: 8px;
      cursor: move;
      opacity: 0.3;
      transition: opacity 0.2s;
    }
    .grid-item:hover .drag-handle {
      opacity: 0.7;
    }
  `;

  const [checked, setChecked] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };
  return (
    <>
      <style>{dragStyles}</style>
      <Stack
        direction={"row"}
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <FormControlLabel
          control={<Switch checked={checked} onChange={handleChange} />}
          label={strings.home.editlayout}
        />
      </Stack>
      <Box px={1}>
        <Grid container spacing={2}>
          {gridItems.map((item, index) => (
            <Grid
              key={item.id}
              size={item.size}
              className="grid-item"
              draggable={checked ? "true" : "false"}
              onDragStart={(e: React.DragEvent<HTMLDivElement>) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={(e: React.DragEvent<HTMLDivElement>) => handleDragEnter(e)}
              onDragLeave={handleDragLeave}
              onDrop={(e: React.DragEvent<HTMLDivElement>) => handleDrop(e, index)}
            >
              {checked && (
                <div className="drag-handle">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                  </svg>
                </div>
              )}
              {item.component}
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}

export default Index;
